import { useCallback } from "react";
import useSWR from "swr";
import axios from "axios";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_SUPPLEMENTS_SUBCOLLECTION } from "../../constants/FirestoreConstant";
import { resolveShopApiUrl } from "../../functions/api/shopApi";

/**
 * Fetches every supplement (follow-up payment / refund-due record) across every order via a
 * Firestore collection-group query, instead of a denormalized flag on each order. No status
 * filter here - the overview page filters client-side (status/type/shop), which also means
 * no dedicated Firestore index is needed for this query (a plain, unfiltered collection-group
 * read doesn't require one; only filtered/ordered ones do).
 */
const runQuery = async (firestore, shopSource) => {
  const snapshot = await firestore.collectionGroup(FIRESTORE_SUPPLEMENTS_SUBCOLLECTION).get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data(), shopSource: doc.data().shopSource || shopSource }));
};

const fetchSupplements = async () => {
  // Use allSettled, not all - a permission problem on one shop's Firestore project must
  // not hide valid results from the other, and every failure needs to be visible (logged +
  // surfaced in the UI) instead of silently resulting in an empty list.
  const [vceResult, hvoResult] = await Promise.allSettled([runQuery(vceFirestore, "VCE"), runQuery(hvoFirestore, "HVO")]);

  const results = [
    { shopSource: "VCE", result: vceResult },
    { shopSource: "HVO", result: hvoResult },
  ];

  const failures = results
    .filter(({ result }) => result.status === "rejected")
    .map(({ shopSource, result }) => {
      console.error(`[useSupplementInvoices] ${shopSource} collection-group query failed:`, result.reason);
      return `${shopSource}: ${result.reason?.message || result.reason}`;
    });

  const supplements = results
    .filter(({ result }) => result.status === "fulfilled")
    .flatMap(({ result }) => result.value);

  return { supplements: supplements.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)), failures };
};

const resolveFirestore = (shopSource) => (shopSource === "HVO" ? hvoFirestore : vceFirestore);

export const useSupplementInvoices = () => {
  const { data, error, isLoading, mutate } = useSWR("supplement-invoices", fetchSupplements, {
    revalidateOnFocus: false,
    dedupingInterval: 5000,
  });

  const refresh = useCallback(() => mutate(), [mutate]);

  /**
   * Safety-net action for when a customer paid outside the link (e.g. bank transfer), or for
   * a supplement whose payment link could never be created.
   */
  const markSupplementPaidManually = useCallback(
    async (supplement) => {
      await resolveFirestore(supplement.shopSource)
        .collection("orders")
        .doc(supplement.orderId)
        .collection(FIRESTORE_SUPPLEMENTS_SUBCOLLECTION)
        .doc(supplement.id)
        .update({ status: "manual", paidAt: Date.now() });
      await mutate();
    },
    [mutate],
  );

  const markRefundResolved = useCallback(
    async (supplement) => {
      await resolveFirestore(supplement.shopSource)
        .collection("orders")
        .doc(supplement.orderId)
        .collection(FIRESTORE_SUPPLEMENTS_SUBCOLLECTION)
        .doc(supplement.id)
        .update({ status: "resolved" });
      await mutate();
    },
    [mutate],
  );

  /**
   * Re-sends the payment-link mail for an EXISTING supplement, using its already-created
   * Pay. link - never creates a new transaction. This is the fix for "the customer never
   * actually got the link" (mail failed) without producing a duplicate supplement.
   */
  const resendSupplementPaymentMail = useCallback(
    async (supplement) => {
      if (!supplement.paymentUrl) {
        throw new Error("Dit supplement heeft nog geen betaallink (aanmaken is eerder mislukt).");
      }
      await axios.post(`${resolveShopApiUrl(supplement.shopSource)}/api/email/order/supplement-payment`, {
        orderDocumentId: supplement.orderId,
        supplementId: supplement.id,
        customerEmail: supplement.customerEmail,
        customerFirstName: supplement.customerFirstName,
        customerLastName: supplement.customerLastName,
        orderReference: supplement.orderReference,
        amountCents: supplement.amountCents,
        paymentUrl: supplement.paymentUrl,
        addedItems: supplement.addedItems,
        removedItems: supplement.removedItems,
      });
      await mutate();
    },
    [mutate],
  );

  return {
    supplements: data?.supplements || [],
    fetchFailures: data?.failures || [],
    isLoading,
    error,
    refresh,
    markSupplementPaidManually,
    markRefundResolved,
    resendSupplementPaymentMail,
  };
};
